/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { useParams } from 'react-router-dom';

import { Message } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import FICHE_CONFIG, {
  type DossierActe,
} from '@/components/Acte/Fiche/ficheConfig';
import { baseUrlApp } from '@/routes';
import type { TypeActe } from '@/types';

import ActeDocumentsPage from './ActeDocumentsPage';

/**
 * Point d'entrée de l'écran des documents, monté sur sa propre `Feature`.
 *
 * Cet écran **n'est pas une fiche** : il ne passe donc pas par
 * `FicheContainer`, qui l'enfermerait dans `FicheRouter`, `FicheProvider` et
 * surtout `FicheBoundary` — lequel remplace tout par `null` à la moindre
 * exception. Il charge lui-même le dossier de l'acte, dont il n'a besoin que
 * pour composer le titre.
 */
const ActeDocumentsRoute = () => {
  const { typeActe, identifiant } = useParams<{
    typeActe: TypeActe;
    identifiant: string;
  }>();

  const config = FICHE_CONFIG[typeActe];

  const [dossier, setDossier] = React.useState<DossierActe | null>(null);

  React.useEffect(() => {
    if (!config?.url || !identifiant) return undefined;

    let vivant = true;

    request
      .get<unknown, DossierActe>(`${config.url}/${identifiant}`)
      .then((data) => {
        if (vivant) setDossier(data);
      })
      .catch(() => {
        // Le titre est un confort : son absence ne doit pas priver l'utilisateur
        // de ses documents.
        if (vivant) setDossier(null);
      });

    return () => {
      vivant = false;
    };
  }, [config?.url, identifiant]);

  if (!config) {
    return (
      <Message error content={`Type d'acte non pris en charge : ${typeActe}`} />
    );
  }

  return (
    <ActeDocumentsPage
      identifiantActe={Number(identifiant)}
      typeActe={typeActe}
      titre={dossier ? config.getTitre(dossier) : undefined}
      retour={`${baseUrlApp}${typeActe}/${identifiant}`}
    />
  );
};

export default ActeDocumentsRoute;
